



import React, { useState, useEffect, useCallback } from "react";
import APIConfig from "../../Common/Configurations/APIConfig";
import Navbar from "../../Navigation/Navbar";
import Swal from "sweetalert2";
import { 
  Pencil, Trash2, Plus, Search, ChevronLeft, ChevronRight, 
  X, Save, Loader2, RotateCcw, Briefcase
} from "lucide-react";

const DesignationManager = () => {
  // --- DATA STATES ---
  const [designations, setDesignations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [inputValue, setInputValue] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const pageSize = 10;

  // --- MODAL STATES ---
  const [isFormModalOpen, setIsFormModalOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [saving, setSaving] = useState(false);
  
  // --- FORM STATE (Simplified to only Name) ---
  const [formData, setFormData] = useState({
    designationName: ""
  });

  // --- API CALLS ---
  const fetchDesignations = useCallback(async () => {
    setLoading(true);
    try {
      const url = `${APIConfig.APIACTIVEURL}${APIConfig.DESIGNATION_GET_ALL}?pageNumber=${currentPage}&pageSize=${pageSize}&searchTerm=${searchTerm}`;
      const response = await fetch(url);
      const result = await response.json();
      setDesignations(result.data || []);
      setTotalPages(result.totalPages || 1);
    } catch (e) {
      console.error(e);
      setDesignations([]);
    }
    setLoading(false);
  }, [currentPage, searchTerm]);

  useEffect(() => { fetchDesignations(); }, [fetchDesignations]);

  // --- HANDLERS ---
  const handleSearchTrigger = () => { setSearchTerm(inputValue); setCurrentPage(1); };
  const handleClearSearch = () => { setInputValue(""); setSearchTerm(""); setCurrentPage(1); };

  const handleOpenCreate = () => {
    setEditingId(null);
    setFormData({ designationName: "" });
    setIsFormModalOpen(true);
  };

  const handleOpenEdit = (d) => {
    setEditingId(d.designationId);
    setFormData({ designationName: d.designationName });
    setIsFormModalOpen(true);
  };

  const handleFormSubmit = async (e) => {
    e.preventDefault();
    if (!formData.designationName) return Swal.fire("Required", "Please enter a designation name.", "warning");
    
    setSaving(true);
    try {
      const method = editingId ? "PUT" : "POST";
      const url = editingId 
        ? `${APIConfig.APIACTIVEURL}${APIConfig.DESIGNATION_BASE}/${editingId}` 
        : `${APIConfig.APIACTIVEURL}${APIConfig.DESIGNATION_BASE}`;

      const res = await fetch(url, {
        method: method,
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ 
            designationId: editingId, 
            designationName: formData.designationName 
        }),
      });

      if (res.ok) {
        setIsFormModalOpen(false);
        fetchDesignations();
        Swal.fire("Success", editingId ? "Updated successfully" : "Created successfully", "success");
      } else {
        const err = await res.json();
        Swal.fire("Error", err.message || "Failed to save", "error");
      }
    } catch (e) { console.error(e); }
    setSaving(false);
  };

  const handleDelete = async (id) => {
    const result = await Swal.fire({ 
        title: 'Delete Designation?', 
        text: "This action cannot be undone.",
        icon: 'warning', 
        showCancelButton: true,
        confirmButtonColor: '#ef4444'
    });
    if (result.isConfirmed) {
      try {
        const res = await fetch(`${APIConfig.APIACTIVEURL}${APIConfig.DESIGNATION_BASE}/${id}`, { method: "DELETE" });
        if(res.ok) {
            fetchDesignations();
            Swal.fire("Deleted!", "Designation removed.", "success");
        }
      } catch (e) { console.error(e); }
    }
  };

  // --- STYLES ---
  const styles = {
    container: { padding: "40px 60px", backgroundColor: "#f4f7fe", minHeight: "100vh", fontFamily: "'Inter', sans-serif" },
    header: { display: "flex", justifyContent: "space-between", alignItems: "flex-end", marginBottom: "35px" },
    title: { fontSize: "30px", fontWeight: "900", color: "#1b2559", margin: 0 },
    card: { backgroundColor: "#fff", borderRadius: "24px", border: "1px solid #f1f5f9", overflow: "hidden", boxShadow: "0 4px 20px rgba(0,0,0,0.02)" },
    th: { padding: "18px 24px", backgroundColor: "#f8fafc", color: "#a3aed0", fontSize: "12px", fontWeight: "800", textTransform: "uppercase", textAlign: "left" },
    td: { padding: "20px 24px", borderBottom: "1px solid #f8fafc", verticalAlign: "middle" },
    modalOverlay: { position: "fixed", inset: 0, backgroundColor: "rgba(0,0,0,0.4)", backdropFilter: "blur(6px)", display: "flex", justifyContent: "center", alignItems: "center", zIndex: 1000 },
    modalContent: { backgroundColor: "#fff", borderRadius: "28px", width: "450px", padding: "40px", position: "relative", boxShadow: "0 25px 50px rgba(0,0,0,0.15)" },
    input: { width: "100%", padding: "14px 16px", borderRadius: "12px", border: "1.5px solid #e2e8f0", outline: "none", fontSize: "14px", boxSizing: "border-box" },
    label: { display: "block", fontSize: "12px", fontWeight: "800", color: "#a3aed0", marginBottom: "8px", textTransform: "uppercase" },
    btnPrimary: { backgroundColor: "#4f46e5", color: "#fff", padding: "12px 24px", borderRadius: "12px", border: "none", fontWeight: "700", cursor: "pointer", display: "flex", alignItems: "center", gap: "8px" },
    paginationBtn: { padding: "8px 12px", borderRadius: "8px", border: "1px solid #e2e8f0", backgroundColor: "#fff", cursor: "pointer", display: "flex", alignItems: "center" }
  };

  return (
    <>
      <Navbar />
      <div style={styles.container}>
        <div style={styles.header}>
          <div>
            <h1 style={styles.title}>Designations</h1>
            <p style={{ color: "#a3aed0", margin: "5px 0 0 0", fontSize: "15px" }}>Define and manage organization roles.</p>
          </div>
          <button style={styles.btnPrimary} onClick={handleOpenCreate}><Plus size={20}/> Add New Designation</button>
        </div>

        {/* Search Bar */}
        <div style={{ display: "flex", gap: "12px", marginBottom: "30px" }}>
          <div style={{ position: "relative", flex: 1, maxWidth: "450px" }}>
            <Search size={18} style={{ position: "absolute", left: "15px", top: "15px", color: "#cbd5e1" }} />
            <input style={{ ...styles.input, paddingLeft: "45px" }} placeholder="Search designation name..." value={inputValue} 
              onChange={(e) => setInputValue(e.target.value)} onKeyDown={(e) => e.key === 'Enter' && handleSearchTrigger()} />
          </div>
          <button style={styles.btnPrimary} onClick={handleSearchTrigger}>Search</button>
          
          {searchTerm && (
            <button onClick={handleClearSearch} style={{ backgroundColor: "#fff", color: "#64748b", padding: "0 18px", borderRadius: "12px", border: "1.5px solid #e2e8f0", fontWeight: "600", cursor: "pointer", display: "flex", alignItems: "center", gap: "8px" }}>
              <RotateCcw size={18}/> Reset
            </button>
          )}
        </div>

        <div style={styles.card}>
          <table style={{ width: "100%", borderCollapse: "collapse" }}>
            <thead>
              <tr>
                <th style={styles.th}>Designation Name</th>
                <th style={{ ...styles.th, textAlign: "center" }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan="2" style={{ textAlign: "center", padding: "80px" }}><Loader2 className="animate-spin" /></td></tr>
              ) : (designations.map(d => (
                <tr key={d.designationId}>
                  <td style={styles.td}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                        <div style={{ padding: '10px', backgroundColor: '#eef2ff', borderRadius: '10px', color: '#4f46e5' }}>
                            <Briefcase size={18} />
                        </div>
                        <div style={{ fontWeight: "700", color: "#1b2559", fontSize: '16px' }}>{d.designationName}</div>
                    </div>
                  </td>
                  <td style={{ ...styles.td, textAlign: "center" }}>
                    <div style={{ display: "flex", gap: "10px", justifyContent: "center" }}>
                      <button onClick={() => handleOpenEdit(d)} style={{ border: "none", background: "#eef2ff", color: "#4f46e5", padding: "10px", borderRadius: "10px", cursor: "pointer" }}><Pencil size={16}/></button>
                      <button onClick={() => handleDelete(d.designationId)} style={{ border: "none", background: "#fef2f2", color: "#ef4444", padding: "10px", borderRadius: "10px", cursor: "pointer" }}><Trash2 size={16}/></button>
                    </div>
                  </td>
                </tr>
              )))}
              {!loading && designations.length === 0 && (
                  <tr><td colSpan="2" style={{textAlign: 'center', padding: '40px', color: '#a3aed0'}}>No designations found.</td></tr>
              )}
            </tbody>
          </table>

          {/* Pagination */}
          <div style={{ padding: "20px 24px", display: "flex", justifyContent: "space-between", alignItems: "center", borderTop: "1px solid #f8fafc" }}>
            <span style={{ fontSize: "14px", color: "#a3aed0", fontWeight: "600" }}>Page {currentPage} of {totalPages}</span>
            <div style={{ display: "flex", gap: "8px" }}>
              <button disabled={currentPage === 1} onClick={() => setCurrentPage(prev => prev - 1)} style={{ ...styles.paginationBtn, opacity: currentPage === 1 ? 0.5 : 1 }}><ChevronLeft size={18}/></button>
              <button disabled={currentPage === totalPages} onClick={() => setCurrentPage(prev => prev + 1)} style={{ ...styles.paginationBtn, opacity: currentPage === totalPages ? 0.5 : 1 }}><ChevronRight size={18}/></button>
            </div>
          </div>
        </div>

        {/* --- ADD/EDIT MODAL --- */}
        {isFormModalOpen && (
          <div style={styles.modalOverlay}>
            <div style={styles.modalContent}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "30px" }}>
                <h3 style={{ margin: 0, fontSize: "22px", fontWeight: "900", color: "#1b2559" }}>{editingId ? "Update Designation" : "Add Designation"}</h3>
                <button onClick={() => setIsFormModalOpen(false)} style={{ background: "none", border: "none", cursor: "pointer", color: "#cbd5e1" }}><X size={24}/></button>
              </div>
              
              <form onSubmit={handleFormSubmit}>
                <div style={{ marginBottom: "25px" }}>
                  <label style={styles.label}>Designation Name *</label>
                  <div style={{ position: "relative" }}>
                    <Briefcase size={18} style={{ position: "absolute", left: "15px", top: "14px", color: "#cbd5e1" }} />
                    <input 
                      style={{ ...styles.input, paddingLeft: "45px" }} 
                      placeholder="e.g. Accountant, Manager..." 
                      value={formData.designationName} 
                      onChange={(e) => setFormData({ designationName: e.target.value })}
                      required
                    />
                  </div>
                </div>

                <div style={{ display: "flex", justifyContent: "flex-end", gap: "15px", borderTop: "1px solid #f1f5f9", paddingTop: "30px" }}>
                  <button type="button" onClick={() => setIsFormModalOpen(false)} style={{ background: "none", border: "1px solid #e2e8f0", padding: "12px 25px", borderRadius: "12px", fontWeight: "600", cursor: "pointer" }}>Cancel</button>
                  <button type="submit" disabled={saving} style={{ ...styles.btnPrimary, padding: "12px 40px" }}>
                    {saving ? <Loader2 className="animate-spin" size={18}/> : <Save size={18}/>} Save
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}
      </div>
    </>
  );
};

export default DesignationManager;